import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { medicationApi } from 'src/api/medication';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/medicationFormSchema';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import { responsePayload } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import {
  AddFacilityMedicationDto,
  ChangeMedicationStatusDto,
  GetProfileMedicationsDto,
  MedicationApiMedicationControllerGetProfileMedicationsInOrganizationRequest as PatientMedicationsRequest,
  UpdateFacilityMedicationDto,
} from 'woofwoof-api';
import { ScopedPage, sliceInMemory } from './scopedPage';
import { useClinicalQuery } from './useClinicalQuery';
import {
  useMedicationCreateSeries,
  useMedicationEditSeries,
} from './useMedicationWriteSeries';
import { ScopeMutationKind } from './useRemindersScope';
import { useVisitScope } from './useVisitScope';

interface MedicationsScopeOptions {
  page: number;
  rowsPerPage: number;
  /** Server-side on the profile branch, in memory on the visit branch. */
  statuses?: string[];
  onMutationSuccess?: (
    kind: ScopeMutationKind,
    items: GetProfileMedicationsDto[],
  ) => void;
  onMutationError?: (kind: ScopeMutationKind, error: unknown) => void;
  /** The medication the form shows, so the edit's unconfirmed state is its own (R3-4). */
  editingId?: string;
}

export interface MedicationsScope extends ScopedPage<GetProfileMedicationsDto> {
  isLoading: boolean;
  isVisitScoped: boolean;
  /** `values`: what the form held, shown again if the form reopens unconfirmed. */
  create: (
    payload: AddFacilityMedicationDto,
    values?: MedicationsFormModalValuesProps,
  ) => void;
  update: (
    medicationId: string,
    payload: UpdateFacilityMedicationDto,
    values?: MedicationsFormModalValuesProps,
  ) => void;
  remove: (medicationId: string) => void;
  /** Awaitable: a status change is applied before the edit that follows it. */
  changeStatus: (
    medicationId: string,
    payload: ChangeMedicationStatusDto,
  ) => Promise<unknown>;
  /** Refetch, e.g. once a medication's linked reminder has changed. */
  refresh: () => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  /** R3-4: the last create may have committed unseen; the form must resend it. */
  createPending: boolean;
  /** R3-4: the same for the last edit of the medication `editingId` names. */
  updatePending: boolean;
  /** F-8: the create series is held by 422 WAREHOUSE_DISABLED. */
  createBlocked: boolean;
  /** F-8: the same for the edit series. */
  updateBlocked: boolean;
  /** While pending: the values the frozen create was built from. */
  createFrozenValues?: MedicationsFormModalValuesProps;
  /** While pending: the values the frozen edit was built from. */
  updateFrozenValues?: MedicationsFormModalValuesProps;
  /** #688 R1-2: the last create's refusal, for the form to mark its field. */
  createError: unknown;
  /** #688 R1-2: the same for the edit series. */
  updateError: unknown;
}

/**
 * Medications for the current scope — the patient's profile when there is one,
 * otherwise this visit alone. See `useVisitScope`.
 *
 * The visit branch reuses `['visit-medications', visitId]`, the key
 * `VisitCourseCard` already writes, so Przebieg wizyty and the Lekarstwa tab
 * share one cache instead of drifting apart. Both keys are the selected
 * organization's: the answers carry its stock issues (R1-1).
 */
export function useMedicationsScope({
  page,
  rowsPerPage,
  statuses,
  onMutationSuccess,
  onMutationError,
  editingId,
}: MedicationsScopeOptions): MedicationsScope {
  const { profileId, visitId, isVisitScoped } = useVisitScope();
  const queryClient = useQueryClient();
  const onStockWrite = useStockWriteFeedback();

  const profileQuery = useClinicalQuery(
    'patient-medications',
    profileId,
    [page, rowsPerPage, statuses],
    (options) =>
      medicationApi.medicationControllerGetProfileMedicationsInOrganization(
        {
          profileId,
          page,
          limit: rowsPerPage,
          statuses: statuses?.length
            ? (statuses as PatientMedicationsRequest['statuses'])
            : undefined,
        },
        options,
      ),
    {
      retry: 1,
      enabled: !isVisitScoped && Boolean(profileId),
      keepPreviousData: true,
    },
  );

  const visitQuery = useClinicalQuery(
    'visit-medications',
    visitId,
    [],
    (options) =>
      medicationApi.medicationControllerListVisitMedications(
        { visitId: visitId || '' },
        options,
      ),
    { retry: 1, enabled: isVisitScoped && Boolean(visitId) },
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(['patient-medications', profileId]);
    // R4-1: the reminder commits with the medication, so its lists refresh too.
    if (profileId) {
      queryClient.invalidateQueries(['profile-reminders', profileId]);
    }
    if (visitId) {
      queryClient.invalidateQueries(['visit-medications', visitId]);
      queryClient.invalidateQueries(['visit-activity', visitId]);
      queryClient.invalidateQueries(['visit-reminders', visitId]);
    }
  }, [queryClient, profileId, visitId]);

  // The add/update endpoints answer with the freshly-written list; delete and
  // status-change answer with nothing.
  const settle = (kind: ScopeMutationKind) => (response: unknown) => {
    invalidate();
    const items = responsePayload(response);
    onMutationSuccess?.(
      kind,
      Array.isArray(items) ? (items as GetProfileMedicationsDto[]) : [],
    );
  };

  const fail = (kind: ScopeMutationKind) => (error: unknown) =>
    onMutationError?.(kind, error);

  // R3-4: every create and edit travels in a series, with or without stock;
  // an unresolved one resends its frozen request. The endpoint: the
  // patient's when there is one, the visit's otherwise.
  const route = patientOrVisitRoute(profileId, visitId);
  const createSeries = useMedicationCreateSeries({
    route,
    onSuccess: settle('create'),
    onError: fail('create'),
  });
  const updateSeries = useMedicationEditSeries({
    route,
    editingId: editingId ?? '',
    onSuccess: settle('update'),
    onError: fail('update'),
  });

  // Explicit `Promise<unknown>`: the two branches resolve to differently-typed
  // AxiosResponses (void vs the refreshed list), and the resulting union defeats
  // useMutation's overload resolution.
  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: (medicationId: string): Promise<unknown> =>
      isVisitScoped
        ? medicationApi.medicationControllerRemoveVisitMedication({
            visitId: visitId || '',
            medicationId,
          })
        : medicationApi.medicationControllerDeleteProfileMedicationInOrganization(
            { profileId, medicationId, visitId: visitId || undefined },
          ),
    // A delete reverses the medication's stock.
    onSuccess: (response) => {
      onStockWrite(response);
      settle('delete')(response);
    },
    onError: fail('delete'),
  });

  const { mutateAsync: statusMutate } = useMutation({
    mutationFn: ({
      medicationId,
      payload,
    }: {
      medicationId: string;
      payload: ChangeMedicationStatusDto;
    }): Promise<unknown> =>
      isVisitScoped
        ? medicationApi.medicationControllerChangeVisitMedicationStatus({
            visitId: visitId || '',
            medicationId,
            changeMedicationStatusDto: payload,
          })
        : medicationApi.medicationControllerChangeProfileMedicationStatusInOrganization(
            { profileId, medicationId, changeMedicationStatusDto: payload },
          ),
    onSuccess: invalidate,
    onError: fail('update'),
  });

  // The visit-scoped list is unpaginated and unfiltered, so the status filter
  // that the profile endpoint applies server-side is applied here instead.
  const visitItems = (visitQuery.data?.data ?? []).filter((m: any) =>
    statuses?.length ? statuses.includes(m.status) : true,
  );

  const paged: ScopedPage<GetProfileMedicationsDto> = isVisitScoped
    ? sliceInMemory(visitItems, page, rowsPerPage)
    : {
        items: profileQuery.data?.data.data ?? [],
        total: profileQuery.data?.data.total ?? 0,
      };

  return {
    ...paged,
    isLoading: isVisitScoped ? visitQuery.isLoading : profileQuery.isLoading,
    isVisitScoped,
    create: (payload, values) => createSeries.submit(null, payload, values),
    update: (medicationId, payload, values) =>
      updateSeries.submit(medicationId, payload, values),
    remove: deleteMutate,
    changeStatus: useCallback(
      (medicationId: string, payload: ChangeMedicationStatusDto) =>
        statusMutate({ medicationId, payload }),
      [statusMutate],
    ),
    refresh: invalidate,
    isCreating: createSeries.isLoading,
    isUpdating: updateSeries.isLoading,
    isDeleting,
    createPending: createSeries.pending,
    updatePending: updateSeries.pending,
    createBlocked: createSeries.blocked,
    updateBlocked: updateSeries.blocked,
    createFrozenValues: createSeries.frozenValues,
    updateFrozenValues: updateSeries.frozenValues,
    createError: createSeries.lastError,
    updateError: updateSeries.lastError,
  };
}
